import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, formatBytes, formatDate, type Historial, type Ubicacion } from './api';
import { Confirmar, Modal } from './ui';

const EVENTOS: Record<string, string> = {
  open: 'Abrió',
  save: 'Guardó',
  restore: 'Restauró',
  download: 'Descargó',
};

export function HistorialDialog({
  ubicacion,
  nombre,
  onClose,
}: {
  ubicacion: Ubicacion;
  nombre: string;
  onClose: () => void;
}) {
  const [datos, setDatos] = useState<Historial | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [porRestaurar, setPorRestaurar] = useState<number | null>(null);
  const [pestania, setPestania] = useState<'versiones' | 'actividad'>('versiones');

  const cargar = useCallback(async () => {
    try {
      setDatos(await api.historial(ubicacion));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos cargar el historial.');
    }
  }, [ubicacion]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function descargar(id: number) {
    try {
      const { url } = await api.descargarVersion(id);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos descargar la versión.');
    }
  }

  async function restaurar(id: number) {
    const version = datos?.versiones.find((v) => v.id === id);
    setPorRestaurar(null);
    try {
      const { resultado } = await api.restaurarVersion(id);
      setAviso(
        resultado === 'sin-cambios'
          ? 'Esa versión es idéntica a la actual: no hubo nada que restaurar.'
          : `Se restauró la versión ${version?.numero}. La que había quedó guardada como versión nueva.`,
      );
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No pudimos restaurar la versión.');
    }
  }

  return (
    <Modal
      titulo="Historial"
      subtitulo={nombre}
      onClose={onClose}
      acciones={
        <button className="secundario" onClick={onClose}>
          Cerrar
        </button>
      }
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {aviso && <p className="aviso-ok">{aviso}</p>}

      {!datos && !error && <p className="vacio-chico">Cargando…</p>}

      {datos && (
        <>
          {datos.ultimoEditor && (
            <p className="historial-resumen">
              Última edición de <strong>{datos.ultimoEditor}</strong> el{' '}
              {formatDate(datos.ultimaEdicion)} · editado {datos.vecesEditado}{' '}
              {datos.vecesEditado === 1 ? 'vez' : 'veces'}
            </p>
          )}

          <div className="subpestanias" role="tablist">
            <button
              role="tab"
              aria-selected={pestania === 'versiones'}
              className={pestania === 'versiones' ? 'activa' : ''}
              onClick={() => setPestania('versiones')}
            >
              Versiones ({datos.versiones.length})
            </button>
            <button
              role="tab"
              aria-selected={pestania === 'actividad'}
              className={pestania === 'actividad' ? 'activa' : ''}
              onClick={() => setPestania('actividad')}
            >
              Actividad
            </button>
          </div>

          {pestania === 'versiones' &&
            (datos.versiones.length === 0 ? (
              <p className="vacio-chico">
                Todavía no hay versiones anteriores. Se crea una cada vez que alguien guarda
                cambios en el editor.
              </p>
            ) : (
              <ul className="lista-versiones">
                {datos.versiones.map((v) => (
                  <li key={v.id}>
                    <div className="version-datos">
                      <strong>Versión {v.numero}</strong>
                      <span>
                        {v.autor} · {formatDate(v.fecha)} · {formatBytes(v.sizeBytes)}
                      </span>
                    </div>
                    <div className="version-acciones">
                      <button className="link" onClick={() => descargar(v.id)}>
                        Descargar
                      </button>
                      {datos.puedeRestaurar && (
                        <button className="link" onClick={() => setPorRestaurar(v.id)}>
                          Restaurar
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ))}

          {pestania === 'actividad' &&
            (datos.eventos.length === 0 ? (
              <p className="vacio-chico">Sin actividad registrada.</p>
            ) : (
              <ul className="lista-actividad">
                {datos.eventos.map((e, i) => (
                  <li key={i}>
                    <span className="evento-tipo">{EVENTOS[e.tipo] ?? e.tipo}</span>
                    <span>
                      {e.actor}
                      {e.detalle ? ` · ${e.detalle}` : ''}
                    </span>
                    <span className="evento-fecha">{formatDate(e.fecha)}</span>
                  </li>
                ))}
              </ul>
            ))}
        </>
      )}

      {porRestaurar !== null && (
        <Confirmar
          titulo="Restaurar versión"
          mensaje={`El archivo vuelve a la versión ${
            datos?.versiones.find((v) => v.id === porRestaurar)?.numero
          }. Lo que hay ahora no se pierde: queda guardado como una versión más.`}
          etiquetaAceptar="Restaurar"
          onCancelar={() => setPorRestaurar(null)}
          onAceptar={() => restaurar(porRestaurar)}
        />
      )}
    </Modal>
  );
}
